import React, { useContext, useState, useEffect, useCallback, useMemo } from "react"
import Auth from "@/config/auth"
import Account from "@/config/account"
import { useRouter } from "@/utils/router"
import Cart from "@/config/cart"
import Chat from "@/config/chat"
import Notification from "@/config/notifications"
import { setLoggedOutState as setHttpLoggedOutState } from "@/config/http"

const DataContext = React.createContext()
export const useContextElement = () => useContext(DataContext)

const DEFAULT_COUNTER = { cart: 0, notification: 0, conversation: 0 }

export default function Context({ children }) {
  const router = useRouter()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [counter, setCounter] = useState(DEFAULT_COUNTER)
  const [loggedOutState, setLoggedOutState] = useState(false)

  const performLogout = useCallback(() => {
    setLoggedOutState(true)
    setIsAuthenticated(false)
    setUser(null)
    setCounter(DEFAULT_COUNTER)

    setHttpLoggedOutState(true)

    localStorage.removeItem("uid")
    localStorage.removeItem("otpData")
    localStorage.removeItem("registerData")
    localStorage.removeItem("transactionTab")

    router.push("/")
  }, [router])

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await Account.getProfile()
      if (!response?.success) {
        setLoggedOutState(true)
        setHttpLoggedOutState(true)
        return { success: false }
      }

      const userModules = response.data?.user?.modules || [];
      const hasAccess = userModules.includes("buyer") || userModules.includes("seller");

      if (!hasAccess && response.data?.role !== "buyer" && response.data?.role !== "seller") {
        try {
          await Auth.logout()
        } catch (error) {
          console.error("Error during forced logout:", error)
        }
        performLogout()
        return { success: false, error: "Unauthorized" }
      }

      setUser(response.data)
      setLoggedOutState(false)
      setHttpLoggedOutState(false)
      return { success: true, response }
    } catch (error) {
      setLoggedOutState(true)
      setHttpLoggedOutState(true)
      return { success: false }
    }
  }, [performLogout])

  const fetchUserCount = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const [
        cartCountResponse,
        conversationCountResponse,
        notificationCountResponse,
      ] = await Promise.all([
        Cart.getCartCount(),
        Chat.getConversationCount(),
        Notification.getNotificationCount(),
      ])

      const nextCounter = {
        cart: cartCountResponse?.success ? cartCountResponse.data : 0,
        conversation: conversationCountResponse?.success
          ? conversationCountResponse.data
          : 0,
        notification: notificationCountResponse?.success
          ? notificationCountResponse.data.all || 0
          : 0,
      }

      setCounter(nextCounter)
      return { success: true, message: "Counts fetched successfully" }
    } catch (error) {
      console.error("Error fetching counts:", error)
      return { success: false }
    }
  }, [isAuthenticated])

  const addToCart = useCallback(
    async (cartData) => {
      try {
        const response = await Cart.addCart(cartData)
        if (response?.success) {
          await fetchUserCount()
        }
        return response
      } catch (error) {
        return error
      }
    },
    [fetchUserCount]
  )

  const updateCart = useCallback(
    async (cartId, cartData) => {
      try {
        const response = await Cart.updateCart(cartId, cartData[0])
        await fetchUserCount()
        return response
      } catch (error) {
        return { success: false, message: error.message }
      }
    },
    [fetchUserCount]
  )

  const finalizeLoginFromResponse = useCallback(
    async (response) => {
      if (!response?.success) {
        return { success: false, error: response?.message || "Login failed" }
      }

      const userModules = response.data?.user?.modules || [];
      const hasAccess = userModules.includes("buyer") || userModules.includes("seller");

      if (!hasAccess && response.data?.role !== "buyer" && response.data?.role !== "seller") {
        try {
          await Auth.logout()
        } catch (error) {
          console.error("Error during forced logout:", error)
        }
        performLogout()
        return { success: false, error: "Unauthorized" }
      }

      let userData = response.data
      try {
        const profileResponse = await Account.getProfile()

        if (profileResponse?.success && profileResponse.data) {
          userData = profileResponse.data

          if (userData.id) {
            localStorage.setItem("uid", userData.id.toString())
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error)
      }

      setUser(userData)
      setIsAuthenticated(true)
      setLoggedOutState(false)
      setHttpLoggedOutState(false)

      try {
        const [cartCountResponse, conversationCountResponse, notificationCountResponse] =
          await Promise.all([
            Cart.getCartCount(),
            Chat.getConversationCount(),
            Notification.getNotificationCount(),
          ])

        setCounter({
          cart: cartCountResponse?.success ? cartCountResponse.data : 0,
          conversation: conversationCountResponse?.success ? conversationCountResponse.data : 0,
          notification: notificationCountResponse?.success ? notificationCountResponse.data.all || 0 : 0,
        })
      } catch (error) {
        console.error("Error fetching counts after login:", error)
      }

      return { success: true }
    },
    [performLogout]
  )

  const handleLogin = useCallback(
    async (loginData) => {
      try {
        const response = await Auth.postLogin({ params: loginData })
        return await finalizeLoginFromResponse(response)
      } catch (error) {
        return { success: false, error: "Login failed" }
      }
    },
    [finalizeLoginFromResponse]
  )

  const handleSocialLogin = useCallback(
    async (socialData) => {
      try {
        const response = await Auth.postSocial(socialData)
        return await finalizeLoginFromResponse(response)
      } catch (error) {
        return { success: false, error: "Login failed" }
      }
    },
    [finalizeLoginFromResponse]
  )

  const handleLogout = useCallback(async () => {
    try {
      await Auth.logout()
      performLogout()
      return { success: true }
    } catch (error) {
      performLogout()
      return { success: false }
    }
  }, [performLogout])

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (loggedOutState) {
          setIsLoading(false)
          return
        }

        const uid = localStorage.getItem("uid")
        if (!uid) {
          setIsLoading(false)
          setLoggedOutState(true)
          setHttpLoggedOutState(true)
          return
        }

        try {
          const response = await Account.getProfile()

          if (response?.success && response.data) {
            const userModules = response.data?.user?.modules || [];
            if (
              userModules.includes("buyer") ||
              userModules.includes("seller") ||
              response.data?.role === "buyer" ||
              response.data?.role === "seller"
            ) {
              setUser(response.data)
              setIsAuthenticated(true)
              setLoggedOutState(false)
              setHttpLoggedOutState(false)

              if (response.data.id) {
                localStorage.setItem("uid", response.data.id.toString())
              }
            } else {
              try {
                await Auth.logout()
              } catch (e) {
                console.error("Init logout error:", e)
              }
              localStorage.removeItem("uid")
              setIsAuthenticated(false)
              setLoggedOutState(true)
              setHttpLoggedOutState(true)
              setUser(null)
            }
          } else {
            localStorage.removeItem("uid")
            setIsAuthenticated(false)
            setLoggedOutState(true)
            setHttpLoggedOutState(true)
            setUser(null)
          }
        } catch (error) {
          localStorage.removeItem("uid")
          setIsAuthenticated(false)
          setLoggedOutState(true)
          setHttpLoggedOutState(true)
          setUser(null)
        }
      } finally {
        setIsLoading(false)
      }
    }
    initializeAuth()
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserCount()
    }
  }, [isAuthenticated, fetchUserCount])

  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    setUser,
    login: handleLogin,
    loginWithSocial: handleSocialLogin,
    logout: handleLogout,
    addProductToCart: addToCart,
    updateCartProduct: updateCart,
    counter,
    setCounter,
    fetchUserCount,
  }), [
    isAuthenticated,
    isLoading,
    user,
    handleLogin,
    handleSocialLogin,
    handleLogout,
    addToCart,
    updateCart,
    counter,
    fetchUserCount,
  ])

  return (
    <DataContext.Provider value={contextValue}>
      {!isLoading && children}
    </DataContext.Provider>
  )
}
